/**
 * Layout: Styleguide
 * -----------------------------------------------------------------------------
 * Core entry file to register and display elements on the styleguide layout
 * - This entire styleguide layout is built by Vue.js
 */
import cookies from 'js-cookie';
import '../../styles/styleguide.scss';

import '../helpers/lazysizes';
import EventBus from '../helpers/event-bus';

/**
 * Components
 */
import Accordion from '../components/accordion';
import QuantitySelector from '../components/quantity-selector';
import ProductGallery from '../components/product-gallery';

/**
* Global Frame utils
*/
window.Frame = {};
window.Frame.EventBus = EventBus;

const Vue = window.Vue || {};

Vue.component('sidebar', {
  data() {
    return {
      isActive: !(cookies.get('frame.styleguide_drawer') === 'false'),
      menu: [],
    };
  },

  mounted() {
    this.menu = [...document.querySelectorAll('.content-section')].map((section) => {
      return {
        title: section.id,
        links: [...section.querySelectorAll('.main-content')].map((template) => {
          return template.dataset.title;
        }),
      };
    });

    this.$root.$on('navigation:toggle', () => {
      this.toggleMenu();
    });
  },

  methods: {
    toggleMenu() {
      this.isActive = !this.isActive;
      cookies.set('frame.styleguide_drawer', this.isActive, 150, '/', Shopify.shop);
    },

    toggleContent(title) {
      this.$root.$emit('updateView', title);
    },
  },

  filters: {
    handleize(value) {
      return value.toLowerCase().replace(' ', '-');
    },

    hash(value) {
      return `#${value}`;
    },

    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  template: `
    <nav :class="['side-nav', {' is-active': isActive }]">
      <slot name="header"></slot>

      <button class="side-nav__toggle-button button button--white" @click.prevent="toggleMenu">
        <slot name="toggle"></slot>
      </button>

      <ul class="side-nav__main-nav" v-for="item in menu">
        <li class="side-nav__item">
          <p class="side-nav__subheading">{{ item.title | capitalize }}</p>

          <ul class="side-nav__sub-nav">
            <li class="side-nav__sub-item" v-for="link in item.links">
              <a class="sub-nav__item-link" :href="link | handleize | hash" @click="toggleContent(link)">{{ link }}</a>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  `,
});

Vue.component('content-section', {
  props: ['name'],

  template: `
    <div class="content-section" :id="name">
      <slot></slot>
    </div>
  `,
});

Vue.component('content-block', {
  data() {
    return {
      isActive: false,
    };
  },

  props: [
    'name',
    'description',
    'centered',
  ],

  mounted() {
    this.isActive = (window.location.hash.includes(this.name.toLowerCase().replace(' ', '-')));

    this.$root.$on('updateView', (title) => {
      this.isActive = (this.name === title);
    });
  },

  template: `
    <div :class="['main-content', { 'is-centered': centered }]" :data-ref="name" :data-title="name" v-show="isActive">
      <div class="main-content__hero">
        <div class="container">
          <div class="row">
            <div class="col s12 m6 offset-m3">
              <h2 class="main-content__title d1">{{ name }}</h2>
              <p class="main-content__subtitle"><slot name="description"></slot></p>
            </div>
          </div>
        </div>
      </div>

      <div class="main-content__body">
        <slot></slot>
      </div>
    </div>
  `,
});

Vue.component('swatch', {
  props: [
    'name',
    'value',
  ],

  template: `
    <tr>
      <td class="sg-table__cell sg-table__cell--top">
        <span class="sg-swatch" :style="style"></span>
        <span class="sg-label">
          {{ name | capitalize }}
        </span>
      </td>
      <td>
      <span class="meta">{{ value }}</span><br>
        <span class="caption">$COLOR_{{ name | screamingSnakecase }}</span>
      </td>
    </tr>
  `,

  computed: {
    style() {
      return `background-color: ${this.value}`;
    },
  },

  filters: {
    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    },

    screamingSnakecase(value) {
      return value.toUpperCase();
    },
  },
});

new Vue({
  el: '#app',

  data() {
    return {
      activePage: '',
      activeComponent: {},
    };
  },

  created() {
    window.Frame = window.Frame || {};

    window.addEventListener('keyup', (evt) => {
      if (evt.keyCode === 27) {
        this.$root.$emit('navigation:toggle');
      }
    });
  },

  mounted() {
    this.$root.$on('updateView', (componentTitle) => {
      this.activeComponent = null;

      if (componentTitle === 'Accordion') {
        this.activeComponent = Accordion('.product-accordion').init();
        this.activeComponent = Accordion('.product-accordion-2', {singleOpen: false, aria: false}).init();
      }

      if (componentTitle === 'Quantity Selector') {
        this.activeComponent = new QuantitySelector('QuantitySelector-1').init();
      }

      if (componentTitle === 'Product Gallery') {
        this.activeComponent = new ProductGallery('.product-gallery');
      }
    });
  },
});
